import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { ZoomIn, ZoomOut, RotateCw, ExternalLink, FileWarning, Loader2, RefreshCw } from 'lucide-react';
import { fileUrl } from './api';

const Btn = ({ onClick, children, testId, label }) => (
    <button onClick={onClick} aria-label={label} data-testid={testId} className="p-1.5 rounded-md bg-white/90 border border-gray-200 text-gray-700 hover:bg-white">{children}</button>
);

const ERR = { 404: 'Fichier original introuvable dans le stockage.', 403: "Vous n'avez pas accès à ce document." };

// Chargement à la demande (blob) : erreurs lisibles + Réessayer, aucun chargement infini
function useDocBlob(id) {
    const [st, setSt] = useState({ url: null, err: null, loading: false });
    const [n, setN] = useState(0);
    useEffect(() => {
        if (!id) return undefined;
        let url = null;
        const ctl = new AbortController();
        setSt({ url: null, err: null, loading: true });
        axios.get(fileUrl(id), { withCredentials: true, responseType: 'blob', timeout: 45000, signal: ctl.signal })
            .then(r => { url = URL.createObjectURL(r.data); setSt({ url, err: null, loading: false }); })
            .catch(e => { if (!axios.isCancel(e)) setSt({ url: null, loading: false, err: ERR[e.response?.status] || 'Document momentanément indisponible (réseau ou serveur).' }); });
        return () => { ctl.abort(); if (url) URL.revokeObjectURL(url); };
    }, [id, n]);
    return [st, useCallback(() => setN(x => x + 1), [])];
}

export function DocumentViewer({ doc }) {
    const [zoom, setZoom] = useState(1);
    const [rot, setRot] = useState(0);
    const [{ url, err, loading }, retry] = useDocBlob(doc?.id);
    useEffect(() => { setZoom(1); setRot(0); }, [doc?.id]);
    if (!doc?.id) return <div className="h-full flex items-center justify-center text-sm text-gray-400" data-testid="invoice-doc-missing">Aucun document</div>;
    const pdf = (doc.content_type || '').includes('pdf');
    return (
        <div className="relative h-full bg-gray-100 rounded-xl overflow-hidden" data-testid="invoice-doc-viewer">
            <div className="absolute top-2 right-2 z-10 flex gap-1">
                {!pdf && url && <>
                    <Btn onClick={() => setZoom(z => Math.min(z + 0.5, 5))} testId="invoice-doc-zoom-in" label="Zoomer"><ZoomIn className="w-4 h-4" /></Btn>
                    <Btn onClick={() => setZoom(z => Math.max(z - 0.5, 0.5))} testId="invoice-doc-zoom-out" label="Dézoomer"><ZoomOut className="w-4 h-4" /></Btn>
                    <Btn onClick={() => setRot(r => (r + 90) % 360)} testId="invoice-doc-rotate" label="Pivoter"><RotateCw className="w-4 h-4" /></Btn>
                </>}
                <a href={url || fileUrl(doc.id)} target="_blank" rel="noreferrer" data-testid="invoice-doc-open" className="p-1.5 rounded-md bg-white/90 border border-gray-200 text-gray-700 hover:bg-white" aria-label="Ouvrir l'original"><ExternalLink className="w-4 h-4" /></a>
            </div>
            {loading && <div className="h-full flex items-center justify-center gap-2 text-sm text-gray-500" data-testid="invoice-doc-loading"><Loader2 className="w-5 h-5 animate-spin" />Chargement du document…</div>}
            {err && (
                <div className="h-full flex flex-col items-center justify-center gap-3 text-sm text-red-600 px-6 text-center" data-testid="invoice-doc-error">
                    <FileWarning className="w-6 h-6" />{err}
                    <button onClick={retry} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-red-200 bg-white text-red-700 hover:bg-red-50" data-testid="invoice-doc-retry"><RefreshCw className="w-4 h-4" />Réessayer</button>
                </div>
            )}
            {url && (pdf
                ? <iframe src={url} title="Facture" className="w-full h-full bg-white" data-testid="invoice-doc-pdf" />
                : (
                    <div className="w-full h-full overflow-auto touch-pan-x touch-pan-y" onDoubleClick={() => setZoom(z => (z > 1 ? 1 : 2.5))}>
                        <img src={url} alt="Facture" data-testid="invoice-doc-image"
                            style={{ width: `${zoom * 100}%`, transform: `rotate(${rot}deg)`, transformOrigin: 'center', transition: 'width 150ms ease' }} className="max-w-none mx-auto block" />
                    </div>
                ))}
        </div>
    );
}
